/*
 * Copyright (c) 2016-2018. campoint AG.
 * This file is licensed under the MIT License (https://opensource.org/licenses/MIT).
 */

import Connection from './connection';
import { UploadResult } from './upload-result';
import { ChatEventMap } from './events/chat-event-map';
import { ChatHandler } from './chat-handler';
import { Query } from './query/query';
import { ChargeInfo } from './charging/charge-info';
import { SourceMetrics } from './sources/source-metrics';
import { Intent } from './intent';
import { Limits } from './limits';
import { Abilities } from './abilities';
import { StartStreamConfig } from './start-stream-config';
import { ChatConfig } from './chat-config';
import { ExitCode } from './exit-code';
import { Handler } from './handler';
import { logger } from './logging/logger';
import Response, { ResponseCommand } from './response';
import { SourceSet } from './sources/source-set';
import { getSourceSet } from './sources/source-set-parser';
import { TargetSet } from './targets/target-set';
import { VERSION } from './version';
import { Emoji } from './emoji';
import { Translations } from './translations';
import { User, isUser } from './user';
import { UserEventType } from './user-event-type';
import { UserEvent } from './user-event';
import {
    addPageVisibilityChangedEventListener,
    isPageVisibilityApiSupported,
    isPageHidden,
    removePageVisibilityChangedEventListener,
} from './browser/page-visibility';

const BASE_URL = 'https://vchat.farm1.campoints.net/proxy/';
const CONTROL_SERVLET_URL = 'https://control.farm1.campoints.net/VXControl';

const dataUrlPattern = /(ds[^.]*\.farm1\.campoints\.net).+?dataID=(\d+)/;

function NOT_AVAILABLE(): Promise<any> {
    return Promise.reject({ code: 4000, reason: 'not available' });
}

function sendTelemetry(type: string, params: any, async = true): void {
    logger.log('sendTelemetry.sendTelemetry', type, params);

    let url = `${BASE_URL}clientinfo.php?type=${type}`;
    if (params) {
        for (const key in params) {
            if (Object.prototype.hasOwnProperty.call(params, key)) {
                url += `&${key}=` + encodeURIComponent(params[key]);
            }
        }
    }

    if (type === 'unload' && 'function' === typeof navigator.sendBeacon) {
        navigator.sendBeacon(url);
    } else {
        /* eslint-disable no-undef */
        const ajax = (window as any).XMLHttpRequest
            ? new XMLHttpRequest()
            : new ActiveXObject('Microsoft.XMLHTTP');
        /* eslint-enable no-undef */
        if (ajax) {
            ajax.open('GET', url, async);
            ajax.send();
        }
    }
}

export function getProtocols(included?: string[], excluded?: string[]): string[] {
    const include = included ?? ['h5live', 'hls', 'webrtc', 'rtmp', 'jpeg'];

    const exclude = excluded ?? [];

    return include.filter((v) => !exclude.includes(v));
}

function parseChatFlags(flagsValue: string): string[] {
    return flagsValue.split(',');
}

export class Chat implements EventTarget {
    // dummies
    public addEventListener: <K extends keyof ChatEventMap>(
        type: K,
        listener: (this: Chat, ev: ChatEventMap[K]) => any,
        useCapture?: boolean
    ) => void;
    public removeEventListener: (
        type: string,
        listener?: EventListenerOrEventListenerObject,
        useCapture?: boolean
    ) => void;
    public dispatchEvent: (evt: Event) => boolean;

    public host: {
        name: string;
        imageSrc: string;
    };

    public isGroupChat: boolean;
    public currentUsers?: User[];
    public username: string;

    public id: number;

    private _isInVoyeurMode: boolean;

    public get isInVoyeurMode(): boolean {
        return this._isInVoyeurMode;
    }

    private _assignedChatFlags: string[] = [];
    public get assignedChatFlags(): string[] {
        return this._assignedChatFlags;
    }

    public emojiList: Emoji[];

    public translations: Translations;

    public singleMode: boolean;
    /** When set to true the sender has his or her microphone muted via the sender software. */
    public audioMuted: boolean;
    /** When set to true the sender has blocked the guest from writing. */
    public textMuted: boolean;
    public paused: boolean;

    /**
     * List (object) of abilities. All abilities are boolean values indicating whether or not the current stream offers a certain feature.
     */
    public abilities: Abilities;

    private config: ChatConfig;
    private handler: ChatHandler;
    // The actual connection to the control servlet (streaming backend).
    private connection: Connection;
    private pendingStreamUpdate: any;
    private startStreamConfig: StartStreamConfig;
    private queryingVideoPull: any;
    private queue = [];
    private initState = -1;
    private protocols: string[];

    // Set to true when the chat has been initialized successfully.
    private active = false;
    private uploadMediaUrl: string;

    constructor(config: ChatConfig, handler?: ChatHandler) {
        if (!config.clientId) {
            throw new Error('empty clientId');
        }
        if (!config.host) {
            throw new Error('empty host');
        }
        if (!config.version) {
            throw new Error('empty version');
        }
        if (config.version.length > 10) {
            throw new Error("max length of the property 'version' is 10");
        }

        this.config = config;

        if (handler) {
            this.handler = handler;
        } else {
            const h = new Handler(this);
            if (config.pauseSupport) {
                h.onChatPause = h.onChatPauseHandler;
            }
            const delegate = document.createDocumentFragment();
            ['addEventListener', 'dispatchEvent', 'removeEventListener'].forEach(
                (f) => (this[f] = (...xs): void => delegate[f](...xs))
            );
        }

        this.connection = new Connection(
            config.clientId,
            config.host,
            config.controlServletURL || CONTROL_SERVLET_URL,
            config.forceLongPooling
        );
    }

    public init(): Promise<{ intent: Intent; limits: Limits }> {
        if (this.initState !== -1) {
            return NOT_AVAILABLE();
        }
        this.initState = 0;

        sendTelemetry('init', {
            chtmnumber: this.config.clientId,
            UAC: navigator.appName,
            UAV: navigator.appVersion,
            UAO: navigator.platform,
            AG1: 'dhtml',
            SCR: `${screen.width}x${screen.height} ${screen.availWidth}x${screen.availHeight}`,
        });

        const playerVersion = this.config.playerVersion ? `/P/${this.config.playerVersion}` : '';
        const version = `C/${VERSION}${playerVersion}/${this.config.version}`;
        const messengerProperties = this.config.messengerProperties
            ? JSON.stringify(this.config.messengerProperties)
            : null;

        return this._sendCommand(
            'CMDP_CINIT',
            { version, initData: this.config.initData, messengerProperties },
            this._processInitCommandResponse.bind(this),
            (): void => {
                this.initState = -1;
            }
        );
    }

    private _processInitCommandResponse(response: Response): { intent: Intent; limits: Limits } {
        logger.log('INIT:', response.values);

        this.initState = 1;
        this.active = true;

        const values = response.values;

        this.id = Number.parseInt(values.chatId);

        this.host = {
            name: values.hostlogin,
            imageSrc: values.imgSrc,
        };

        this.username = values.login;
        this.currentUsers = this.parseCurrentUsers(values.publicUsers);
        this.isGroupChat = values.isPublicText == 1;
        this._assignedChatFlags = values.flags ? parseChatFlags(values.flags) : [];

        this.abilities = {
            text: values.canText == 1,
            video: values.canVideo == 1,
            audio: values.hasAudio == 1,
            preview: values.canPreview == 1,
            upstream: values.canCam2Cam == 1,
            cam2cam: values.canCam2Cam == 1,
            single: values.canSingle == 1,
            private: values.canSingle == 1,
            tip: values.canTip == 1,
        };

        const limits: Limits = {
            total: this._getLimit(values, 'timelimit'),
            video: this._getLimit(values, 'videolimit'),
            text: this._getLimit(values, 'textlimit'),
            preview: this._getLimit(values, 'previewlimit'),
        };

        const t: string = (values.chattype || '').toLowerCase();
        const intent = {
            text: t === 'text' || t === 'video',
            video: t === 'voyeur' || t === 'video',
            audio: (t === 'voyeur' || t === 'video') && this.abilities.audio,
            preview: limits.preview > 0,
        };

        this._isInVoyeurMode = t === 'voyeur';

        this.protocols = getProtocols(
            values.formatsIncluded?.split(','),
            values.formatsExcluded?.split(',')
        );

        if (!this.config.ignoreUnload) {
            window.addEventListener('beforeunload', this.unloadHandler);
            window.addEventListener('unload', this.unloadHandler);
        }

        if (isPageVisibilityApiSupported) {
            addPageVisibilityChangedEventListener(this._onPageVisibilityChanged.bind(this));
            this._reportPageVisibility();
        }

        this.audioMuted = values.audioMuted == 1;
        this.uploadMediaUrl = values.uploadMediaUrl;

        if (values.emojiList) {
            this.emojiList = JSON.parse(values.emojiList);
        }
        if (values.translations) {
            this.translations = JSON.parse(values.translations);
        }

        this.connection.startNoop(this._handleNoopResponse.bind(this));
        if (this.queue.length > 0) {
            this.queue.forEach((cmd) => cmd());
            this.queue = [];
        }
        return { intent: intent, limits: limits };
    }

    private parseCurrentUsers(publicUsers?: string): User[] | undefined {
        if (!publicUsers) {
            return;
        }

        let parsed: unknown;
        try {
            parsed = JSON.parse(publicUsers);
        } catch (error) {
            logger.error('CINIT publicUsers: received invalid json', error);
            return;
        }

        if (Array.isArray(parsed)) {
            const parsedArray = parsed as unknown[];

            if (parsedArray.every((i) => isUser(i))) {
                return parsedArray as User[];
            } else {
                logger.error('CINIT publicUsers: Not every element matches the User interface');
            }
        } else {
            logger.error('CINIT publicUsers: Is not an array.', publicUsers);
        }
    }

    /**
     * Ends the chat with the given exitCode and closes the websocket connection, tears down the whole chat setup.
     * @param exitCode - the exitCode send to the server as the close reason
     */
    public close(exitCode: number = ExitCode.USER): Promise<void> {
        if (!this.active) {
            return NOT_AVAILABLE();
        }

        setTimeout(this._close.bind(this, exitCode), 500);

        return new Promise((resolve) => {
            this.connection.send('CMDP_CLOGOUT', { exitCode: exitCode }, () => {
                this._close(exitCode);
                resolve();
            });
        });
    }

    /**
     * This starts the actual stream for the current chat and returns a list of sources that can be passed to a player.
     * @param config -
     * @returns A set of sources available for the given configuration / chat.
     */
    public startStream(config?: StartStreamConfig): Promise<SourceSet> {
        if (!config) {
            config = {};
        }

        if (config.type === 'preview') {
            return this._sendCommand('CMDP_CSTARTPREVIEW', {}, (response) => {
                const values = response.values;
                const protocolsWithoutAudio = this.protocols.filter(
                    (value) => value !== 'mp3' && value !== 'ogg'
                );
                return getSourceSet(values, values, config, protocolsWithoutAudio);
            });
        }

        this.startStreamConfig = config;
        const p = [];
        p.push(
            this._sendCommand('CMDP_CSTARTVIDEOPULL', {}, (response, resolve, reject) => {
                if (response.values.state === 'querying') {
                    this.queryingVideoPull = { resolve, reject, config };
                } else {
                    return response.values;
                }
            })
        );
        if (!config.noAudio) {
            p.push(
                new Promise((resolve) => {
                    this._sendCommand('CMDP_CSTARTAUDIOPULL', {}, (response) => response.values)
                        .then((values) => resolve(values))
                        .catch(() => resolve({}));
                })
            );
        }

        return Promise.all(p).then((values) =>
            getSourceSet(values[0], values[1] || {}, config, this.protocols)
        );
    }

    /**
     * Starts the text stream part of a chat. Text streams are part of LivePreviews and full video chats. If the text stream is not running but the video stream is, the guest is in voyeur mode.
     */
    public startText(): Promise<any> {
        return this._sendCommand('CMDP_CSTARTTEXT');
    }

    /**
     * Starts an upstream (cam 2 cam) part for the chat
     *
     * @returns a list of possible upstream targets
     */
    public startUpstream(): Promise<TargetSet> {
        return this._sendCommand('CMDP_CSTARTVIDEOPUSH', { flashclient: true }, (response) => {
            const dataUrl = response.values.dataURL;
            const parts = dataUrlPattern.exec(dataUrl);
            let webRTCStreamInfo;
            try {
                webRTCStreamInfo = JSON.parse(response.values.mediaWebRtc);
            } catch (error) {
                console.error(error);
            }

            return {
                jpeg: [
                    {
                        stream: dataUrl,
                        width: Number.parseInt(response.values.width),
                        height: Number.parseInt(response.values.height),
                        fps: Number.parseFloat(response.values.fps),
                        quality: Number.parseFloat(response.values.quality) / 100,
                    },
                ],
                rtmp: [
                    {
                        app: `rtmp://${parts[1]}/vxstream`,
                        stream: parts[2],
                    },
                ],
                webrtc: webRTCStreamInfo ? [webRTCStreamInfo] : [],
            };
        });
    }

    /**
     * Stops cam 2 cam
     */
    public stopUpstream(): Promise<any> {
        return this._sendCommand('CMDP_CSTOPVIDEOPUSH');
    }

    /**
     * Starts the private chat mode
     */
    public startSingle(): Promise<any> {
        return this.abilities.private ? this._sendCommand('CMDP_CQUERY_SINGLE') : NOT_AVAILABLE();
    }

    /**
     * send media file.
     * @param file -
     * @param messageKey -
     * @returns UploadResult
     */
    public async sendMediaFile(file: File, messageKey?: string): Promise<UploadResult> {
        return this._sendMediaFile(file.name, file, file.name, messageKey);
    }

    /**
     * send media file.
     * @param file -
     * @param messageKey -
     * @returns UploadResult
     */
    public async sendMedia(
        filename: string,
        blob: Blob,
        messageKey?: string
    ): Promise<UploadResult> {
        return this._sendMediaFile('BLOB', blob, filename, messageKey);
    }

    private async _sendMediaFile(
        name: string,
        data: Blob,
        filename: string,
        messageKey?: string
    ): Promise<UploadResult> {
        const formData = new FormData();
        formData.append(name, data, filename);
        let url =
            this.uploadMediaUrl + '&action=CMDP_CUPLOADMEDIA' + '&format=json' + '&strip=true';

        if (messageKey) {
            url += '&messageKey=' + encodeURIComponent(messageKey);
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: formData,
            });
            const responseText = await response.text();
            const responseObj = JSON.parse(responseText);
            const success: boolean = responseObj.result.code === 2000;
            const messageId = success ? responseObj.values.messageId : undefined;
            return {
                successfull: success,
                error: success ? null : responseObj.result.reason,
                messageId: messageId,
            };
        } catch (error) {
            return {
                successfull: false,
                error: error.message,
            };
        }
    }

    /**
     * Sends a message from the guest to the backend to be displayed in the chat window of the sender.
     *
     * @param text - The message send to the sender
     * @param messageKey -
     * @returns the message id as a string if it exists
     */
    public async sendMessage(text: string, messageKey?: string): Promise<string | undefined> {
        const sendMsg = async () => {
            return await this._sendCommand(
                'CMDP_CMSG',
                {
                    text: text,
                    messageKey: messageKey,
                },
                (resp, resolve, _) => resolve(resp.values.messageId)
            );
        };

        try {
            return await sendMsg();
        } catch (error) {
            console.log(error.constructor.name);
            if (error instanceof Object) {
                if (error.code === 4151) {
                    await this.startText();
                    return sendMsg();
                }
            } else {
                throw error;
            }
        }
    }
    /**
     *  Sends a response to a query (like a private chat request) where key is a string (indicating the actual query) and the value can be of any type.
     * @param key -
     * @param response -
     */
    public sendQueryResponse(key: string, response: any): Promise<any> {
        return this._sendCommand('CMDP_CQUERYUSER', {
            key: key,
            response: response,
        });
    }

    /**
     * Triggers a tip with given amount (cents) on the backend. The remaining video time limit will be adjusted accordingly. The sender will be informed about the tip via a message in the chat window. The gift parameter is an optional string indicating a certain gift type (deprecated).
     *
     * @param amount -
     * @param gift - @deprecated
     */
    public sendTip(amount = 100, gift = ''): Promise<any> {
        return this.abilities.tip
            ? this._sendCommand('CMDP_CTIP', { cent: amount, gift: gift })
            : NOT_AVAILABLE();
    }

    /**
     * Sends the current audio state (muted / unmuted) to the backend to pass it on to the sender and inform him or her about whether or not the guest can actually hear them.
     *
     * @param enabled -
     */
    public sendAudioState(enabled: boolean): Promise<any> {
        const [statusCode, statusMessage] = enabled
            ? [11, 'sound_enabled']
            : [12, 'sound_disabled'];

        return this._sendStatusMessage(statusCode, statusMessage);
    }

    /**
     * Sends an object to the backend for logging certain information.
     * @param metrics -
     */
    public sendMetrics(metrics: SourceMetrics | any): void {
        if (Object.prototype.hasOwnProperty.call(metrics, 'protocol')) {
            sendTelemetry('update', {
                chtmnumber: this.config.clientId,
                DP: metrics.protocol,
            });
        }

        this._sendStatusMessage(16, JSON.stringify(metrics));
    }

    /**
     * Returns information about the one click payment feature
     */
    public getChargeInfo(): Promise<ChargeInfo> {
        return this._sendCommand('CMDP_CQUERY_CHARGE', {}, (response) => {
            const available = response.values.allowed == 'true' || response.values.allowed == 1;
            const autoCharged = response.values.autoCharge || 0;
            return {
                available: available,
                amounts: available
                    ? response.values.amounts.split(',').map((v: string) => Number.parseInt(v, 10))
                    : [],
                currency: available ? response.values.currency : '',
                additionalCurrencies: available
                    ? JSON.parse(response.values.additionalCurrencies)
                    : [],
                autoCharged: autoCharged,
            };
        });
    }

    /**
     * Triggers a one click payment with the given amount (cents).
     *
     * @param amount - The amount to charge
     */
    public sendCharge(amount: number): Promise<any> {
        return this._sendCommand(
            'CMDP_CCHARGE',
            { amount: amount },
            null,
            null,
            (response) => response.values.successfully == 'true'
        );
    }

    private _closeUnload(): void {
        if (this.active) {
            try {
                this.connection.send('CMDP_CLOGOUT', { exitCode: ExitCode.UNLOAD }, () => {
                    /* noop */
                });
            } catch {
                /* noop */
            }
        }
        this._close(ExitCode.UNLOAD, true, false);
    }

    private _close(
        exitCode: number,
        telemetry = true,
        closeConnection = true,
        exitMessage?: string
    ): void {
        if (!this.config.ignoreUnload) {
            window.removeEventListener('beforeunload', this.unloadHandler);
            window.removeEventListener('unload', this.unloadHandler);
        }

        if (isPageVisibilityApiSupported) {
            removePageVisibilityChangedEventListener(this._onPageVisibilityChanged);
        }

        if (this.active) {
            this.active = false;
            if (telemetry) {
                sendTelemetry(
                    'unload',
                    {
                        chtmnumber: this.config.clientId,
                        exitcode: exitCode,
                        comment: exitCode,
                    },
                    false
                );
            }

            if (closeConnection) {
                this.connection.close();
            }

            this.handler.onChatStop(exitCode, exitMessage);
        }
    }

    private _handleNoopResponse(response: Response): void {
        if (response.ok) {
            response.commands.forEach((command) => this._processCommand(command));
        } else if (response.code == 4104) {
            logger.warn('response.code == 4104');
            const searchResult = /exitCode=([\d-]+)/.exec(response.reason);
            const exitCode: number = searchResult
                ? Number.parseInt(searchResult[1], 10)
                : ExitCode.DEFAULT;
            this._close(exitCode > 0 ? exitCode : ExitCode.DEFAULT, false);
        } else if (response === Response.TIMEOUT) {
            this._close(ExitCode.TIMEOUT);
        } else {
            logger.warn('response.code==', response.code);
        }
    }

    private _processCommand(command: ResponseCommand): void {
        logger.log('processCommand', command);
        switch (command.command) {
            case 'CMSG': {
                this._processMessageCommand(command);
                break;
            }

            case 'CEXIT': {
                this._processExitCommand(command);
                break;
            }
            case 'CQUERYUSER': {
                this._processQueryUserCommand(command);

                break;
            }

            case 'CSTARTVIDEOPULL': {
                this._processStartVideoPullCommand(command);
                break;
            }

            case 'CSTOPVIDEOPULL': {
                this._processStopVideoPullCommand(command);
                break;
            }

            case 'CSTOPPREVIEW': {
                this._processStopPreviewCommand();
                break;
            }

            case 'CSTARTAUDIOPULL': {
                this._processStartAudioPullCommand(command);
                break;
            }

            case 'CSTOPAUDIOPULL': {
                this._processStopAudioPullCommand(command);
                break;
            }

            case 'CSTARTTEXT': {
                this._processStartTextCommand();
                break;
            }

            case 'CSTOPTEXT': {
                this._processStopTextCommand(command);
                break;
            }

            case 'UPDATE': {
                // handling limits update
                this._processUpdateCommand(command);

                break;
            }

            case 'PUBLICCHAT': {
                this._processPublicChatCommand(command);
                break;
            }

            default: {
                logger.warn('unknown command');
            }
        }
    }

    private _processPublicChatCommand(command: ResponseCommand): void {
        if (!command.values.user || !command.values.event) {
            logger.warn('PUBLICCHAT cmd: cmd has missing params', command.values);
            return;
        }

        const user = JSON.parse(command.values.user);
        if (!isUser(user)) {
            logger.warn('PUBLICCHAT cmd: user is not a valid user object ');
            return;
        }

        switch (command.values.event) {
            case 'entered': {
                this._addUser(user);
                break;
            }
            case 'left': {
                this._removeUser(user);
                break;
            }

            default:
                logger.warn('PUBLICCHAT cmd: unknown event', command.values.event);
        }
    }

    private _removeUser(user: User): void {
        const index = this.currentUsers.map((u) => u.userName).indexOf(user.userName, 0);
        if (index > -1) {
            this.currentUsers.splice(index, 1);
            this.handler.onUserEvent(new UserEvent(user, UserEventType.Left));
        }
    }

    private _addUser(user: User): void {
        this.currentUsers.push(user);
        this.handler.onUserEvent(new UserEvent(user, UserEventType.Entered));
    }

    private _processQueryUserCommand(command: ResponseCommand): void {
        const choices = [];
        for (let i = 0; ; i++) {
            const btn = `btn${i}`;
            if (!Object.prototype.hasOwnProperty.call(command.values, btn)) {
                break;
            }
            choices.push({
                name: command.values[btn],
                value: i === 0 ? 'no' : 'yes',
                def: command.values.btndef == btn,
            });
        }

        const query: Query = {
            // single|querysingle|...
            key: command.values.key,
            caption: command.values.caption,
            text: command.values.text,
            timeout: command.values.timeout * 1000,
            choices: choices,
        };
        if (Object.prototype.hasOwnProperty.call(command.values, 'price')) {
            query.price = Number.parseInt(command.values.price, 10);
        }

        this.handler.onQuery(query);
    }

    private _processStopTextCommand(command: ResponseCommand): void {
        if (isWaitingForRecharge(command)) {
            this._pause();
        } else {
            if (!this.textMuted) {
                this.handler.onTextMuteUpdate((this.textMuted = true));
            }
        }
    }

    private _processStartTextCommand(): void {
        if (this.textMuted) {
            this.handler.onTextMuteUpdate((this.textMuted = false));
        }

        if (this._isInVoyeurMode) {
            this._isInVoyeurMode = false;

            if (this.handler.onLeftVoyeurMode) {
                this.handler.onLeftVoyeurMode();
            }
        }
    }

    private _processStopAudioPullCommand(command: {
        command: string;
        id: string;
        values: any;
    }): void {
        if (isWaitingForRecharge(command)) {
            this._pause();
        } else {
            if (!this.audioMuted) {
                this.handler.onAudioMuteUpdate((this.audioMuted = true));
            }
        }
    }

    private _processStartAudioPullCommand(command: ResponseCommand): void {
        if (command.values.dataURL) {
            this._updateStream(command.values, false);
        } else if (this.audioMuted) {
            this.handler.onAudioMuteUpdate((this.audioMuted = false));
        }
    }

    private _processStopPreviewCommand(): void {
        this.handler.onLimitUpdate('preview', 0);
    }

    private _processStopVideoPullCommand(command: ResponseCommand): void {
        if (command.values.code == '4107') {
            this._pause();
        } else {
            logger.warn('unexpected CSTOPVIDEOPULL');
            this._close(ExitCode.ERROR);
        }
    }

    private _processStartVideoPullCommand(command: ResponseCommand): void {
        if (this.queryingVideoPull) {
            const q = this.queryingVideoPull;
            this.queryingVideoPull = undefined;
            q.resolve(command.values);
        } else {
            this._updateStream(command.values, true);
        }
    }

    private _processExitCommand(command: ResponseCommand): void {
        this._close(command.values.exitCode, true, true, command.values.exitMessage);
    }

    private _processMessageCommand(command: ResponseCommand): void {
        const messageKey = command.values.messageKey;
        const paramKeys = [
            'gift',
            'tip',
            'imgSrc',
            'imgSrcSet',
            'imgWidth',
            'imgHeight',
            'imgSrcBlurred',
            'audioOgg',
            'audioMp3',
            'audioM4a',
            'videoHls',
            'videoPoster',
            'videoPosterBlurred',
            'mediaType',
            'mediaState',
            'style',
            'msgType',
            'messageId',
        ];
        const params = {};
        for (const key of paramKeys) {
            if (command.values[key]) {
                params[key] = command.values[key];
            }
        }

        console.log(params);
        this.handler.onMessage(command.values.text, command.values.from, messageKey, params);
    }

    private _processUpdateCommand(command: ResponseCommand): void {
        if (this.handler.onLimitUpdate) {
            [
                ['timelimit_remain', 'total'],
                ['videolimit_remain', 'video'],
                ['textlimit_remain', 'text'],
                ['videolimit', 'videolimit'],
            ].forEach((v) => {
                if (Object.prototype.hasOwnProperty.call(command.values, v[0])) {
                    const value = Number.parseInt(command.values[v[0]], 10) * 1000;
                    // eslint-disable-next-line unicorn/prefer-number-properties
                    if (!isNaN(value)) {
                        this.handler.onLimitUpdate(v[1] as keyof Limits, value);
                    }
                }
            });
        }

        if (
            this.handler.onVideoLimitWarningUpdate &&
            Object.prototype.hasOwnProperty.call(command.values, 'videolimit_rest')
        ) {
            const videolimitRest = Number.parseInt(command.values.videolimit_rest, 10) * 1000;

            const belowThreshold = command.values.below_threshold === '1';

            this.handler.onVideoLimitWarningUpdate(belowThreshold, videolimitRest);
        }

        this.updateAbilityIfChanged(command.values, 'canSingle', 'private');
        this.updateAbilityIfChanged(command.values, 'canSingle', 'single');

        this.updateAbilityIfChanged(command.values, 'canText', 'text');

        this.updateAbilityIfChanged(command.values, 'canCam2Cam', 'cam2cam');
        this.updateAbilityIfChanged(command.values, 'canCam2Cam', 'upstream');

        if (Object.prototype.hasOwnProperty.call(command.values, 'isSingle')) {
            const single = command.values.isSingle == 1;
            if (single !== this.singleMode) {
                this.handler.onSingleModeUpdate((this.singleMode = single));
            }
        }

        if (Object.prototype.hasOwnProperty.call(command.values, 'audioMuted')) {
            const muted = command.values.audioMuted == 1;
            if (muted !== this.audioMuted) {
                this.handler.onAudioMuteUpdate((this.audioMuted = muted));
            }
        }

        if (Object.prototype.hasOwnProperty.call(command.values, 'proxyStreamInfo')) {
            const streamOrientation = command.values.proxyStreamInfo;
            this.handler.onStreamOrientationUpdate(streamOrientation);
        }

        if (Object.prototype.hasOwnProperty.call(command.values, 'flags')) {
            this._assignedChatFlags = command.values.flags
                ? parseChatFlags(command.values.flags)
                : [];
        }
    }

    private updateAbilityIfChanged(
        commandValues: any,
        valueName: string,
        abilityToUpdate: keyof Abilities
    ) {
        if (Object.prototype.hasOwnProperty.call(commandValues, valueName)) {
            const value = commandValues[valueName] == 1;
            if (this.abilities[abilityToUpdate] !== value) {
                this.abilities[abilityToUpdate] = value;
                this.handler.onAbilityUpdate(abilityToUpdate, value);
            }
        }
    }

    private _pause(): void {
        logger.log('Chat._pause');
        if (!this.paused) {
            if (this.handler.onChatPause) {
                this.paused = true;

                this.handler.onChatPause();
            } else {
                this._close(ExitCode.DEFAULT);
            }
        }
    }

    private _updateStream(values: any, video: boolean): void {
        let pr = this.pendingStreamUpdate;
        if (!pr) {
            pr = this.pendingStreamUpdate = {};
        }
        pr[video ? 'v' : 'a'] = values;

        const process = (): void => {
            if (pr.t) {
                clearTimeout(pr.t);
            }

            const updatedSourceSet = getSourceSet(
                pr.v,
                pr.a || {},
                this.startStreamConfig || {},
                this.protocols
            );

            if (this.paused) {
                this.paused = false;
                if (this.handler.onChatResume) {
                    this.handler.onChatResume(updatedSourceSet);
                }
            }

            if (this.handler.onStreamSourceUpdate) {
                this.handler.onStreamSourceUpdate(updatedSourceSet);
            }

            this.pendingStreamUpdate = undefined;
        };

        if (pr.v && pr.a) {
            process();
        } else {
            pr.t = setTimeout(() => process(), 100);
        }
    }

    private _onPageVisibilityChanged(): void {
        this._reportPageVisibility();
    }

    private _reportPageVisibility(): void {
        const [statusCode, statusMessage] = !isPageHidden()
            ? [21, 'PAGE_VISIBLE']
            : [20, 'PAGE_HIDDEN'];

        this._sendStatusMessage(statusCode, statusMessage);
    }

    private _sendStatusMessage(code: number, message: string): Promise<any> {
        return this._sendCommand('CMDP_CSTATUS', {
            statusCode: code,
            text: message,
        });
    }

    private _sendCommand(
        command: string,
        params: any = {},
        successFunc?: (response: Response, res?, rej?) => any,
        failFunc?: (Response: Response) => any,
        testFunc?: (response: Response) => boolean
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            const cmd = (): void => {
                this.connection.send(command, params, (response) => {
                    if (response.ok && (!testFunc || testFunc(response))) {
                        if (successFunc) {
                            const result = successFunc(response, resolve, reject);
                            if (result !== undefined) {
                                resolve(result);
                            }
                        } else {
                            resolve();
                        }
                    } else {
                        if (failFunc) {
                            failFunc(response);
                        }
                        reject({
                            code: response.code,
                            reason: response.reason,
                        });
                    }
                });
            };
            if (this.initState === 1 || command === 'CMDP_CINIT') {
                cmd();
            } else {
                logger.log('queueing command', command);
                this.queue.push(cmd);
            }
        });
    }

    private unloadHandler = (): void => this._closeUnload();

    private _getLimit(pvalues, prop: string): number {
        return Object.prototype.hasOwnProperty.call(pvalues, prop)
            ? Number.parseInt(pvalues[prop], 10) * 1000
            : -1;
    }
}

function isWaitingForRecharge(command: { values: any }): boolean {
    return command.values.code == '4107';
}
